import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type { User, AuthContextType, LoginOptions, Auth0UserProfile } from '../types/auth';
import { getTokenStorage, isMockAuthMode, type TokenStorage } from '../services/auth';

/**
 * Auth Context
 * Provides authentication state and methods throughout the app
 * Supports both Auth0 and mock authentication modes
 *
 * **Token Storage Abstraction (B.1.4.2):**
 * Token storage is now abstracted via the TokenStorage interface to prepare
 * for Auth0 httpOnly cookie migration. In mock mode, tokens are stored in
 * localStorage. In production mode, tokens will be managed by httpOnly cookies
 * set by the backend.
 */
const AuthContext = createContext<AuthContextType | null>(null);

// Storage key for persisting user state (mock mode only)
// Note: Token storage is now handled by TokenStorage abstraction
const AUTH_STORAGE_KEY = 'finova_auth';

// Custom claims namespace for Auth0
const CLAIMS_NAMESPACE = 'https://finova.com';

interface StoredAuthState {
  user: User | null;
  token: string | null;
}

/**
 * Check if mock auth mode is enabled.
 * Delegates to the centralized isMockAuthMode utility for consistency.
 */
function useMockAuth(): boolean {
  return isMockAuthMode();
}

/**
 * Map Auth0 user profile to Finova User type
 */
function mapAuth0User(auth0User: Auth0UserProfile): User {
  const result: User = {
    id: auth0User.sub,
    email: auth0User.email || '',
    name: auth0User.name || '',
  };

  // Only set optional properties if they have values
  if (auth0User.picture) {
    result.picture = auth0User.picture;
  }
  if (auth0User.email_verified !== undefined) {
    result.emailVerified = auth0User.email_verified;
  }
  const orgId = auth0User.org_id || auth0User[`${CLAIMS_NAMESPACE}/organization_id`];
  if (orgId) {
    result.organizationId = orgId;
  }
  const roles = auth0User[`${CLAIMS_NAMESPACE}/roles`];
  if (roles && roles.length > 0) {
    result.roles = roles;
  }

  return result;
}

/**
 * Mock Auth Provider Component
 * Used when VITE_USE_MOCK_AUTH=true for local development
 *
 * **Token Storage (B.1.4.2):**
 * Uses the TokenStorage abstraction for storing JWT tokens. User profile
 * data is still stored in AUTH_STORAGE_KEY for session restoration.
 * The TokenStorage handles access/refresh tokens separately.
 */
function MockAuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get token storage instance (memoized singleton)
  const tokenStorage: TokenStorage = getTokenStorage();

  // Check for existing auth state on mount
  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        // Check if we have tokens stored
        const hasValidTokens = tokenStorage.hasTokens();

        // Also check for user profile in auth state
        const stored = localStorage.getItem(AUTH_STORAGE_KEY);
        if (stored && hasValidTokens) {
          const { user: storedUser } = JSON.parse(stored) as StoredAuthState;
          if (storedUser) {
            setUser(storedUser);
          }
        } else if (stored && !hasValidTokens) {
          // User data exists but no tokens - clear stale state
          localStorage.removeItem(AUTH_STORAGE_KEY);
        }
      } catch (err) {
        console.error('Failed to restore auth state:', err);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        tokenStorage.clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    void checkAuth();
  }, [tokenStorage]);

  /**
   * Login with email and password (mock implementation)
   *
   * **Token Storage (B.1.4.2):**
   * Tokens are now stored via the TokenStorage abstraction.
   * User profile is stored separately in AUTH_STORAGE_KEY.
   */
  const login = useCallback(
    async (email?: string, password?: string): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        // Simulate API call delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Demo credentials validation
        // For investor demo: testuser@sistemafinanceiro.com / Demo01/26!
        const DEMO_EMAIL = 'testuser@sistemafinanceiro.com';
        const DEMO_PASSWORD = 'Demo01/26!';

        if (!email || !password) {
          throw new Error('Email and password are required');
        }

        if (email !== DEMO_EMAIL || password !== DEMO_PASSWORD) {
          throw new Error('Invalid credentials. Please use the demo account provided.');
        }

        // Create mock user for development
        const mockUser: User = {
          id: crypto.randomUUID(),
          email,
          name: (email.split('@')[0] ?? email)
            .replace(/[._]/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase()),
          emailVerified: true,
          roles: ['user'],
          createdAt: new Date().toISOString(),
        };

        // Generate mock tokens
        const mockAccessToken = 'mock_jwt_access_' + Date.now();
        const mockRefreshToken = 'mock_jwt_refresh_' + Date.now();

        // Store tokens via TokenStorage abstraction (B.1.4.2)
        tokenStorage.setTokens({
          accessToken: mockAccessToken,
          refreshToken: mockRefreshToken,
        });

        // Store user profile separately (for session restoration)
        const authState: StoredAuthState = {
          user: mockUser,
          token: mockAccessToken, // Legacy field for backward compatibility
        };
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authState));

        setUser(mockUser);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Login failed';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [tokenStorage]
  );

  /**
   * Logout and clear authentication state
   *
   * **Token Storage (B.1.4.2):**
   * Clears tokens via TokenStorage abstraction and user profile separately.
   */
  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Clear tokens via TokenStorage abstraction (B.1.4.2)
      tokenStorage.clearTokens();

      // Clear stored user profile
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setUser(null);
      setError(null);
    } finally {
      setIsLoading(false);
    }
  }, [tokenStorage]);

  /**
   * Clear authentication errors
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Get access token for API calls (mock implementation)
   *
   * **Token Storage (B.1.4.2):**
   * Retrieves the access token from TokenStorage abstraction.
   * In mock mode, this returns the localStorage-stored token.
   * In production (Auth0), this would be replaced by getAccessTokenSilently.
   */
  const getAccessToken = useCallback(async (): Promise<string> => {
    const token = tokenStorage.getAccessToken();
    if (!token) {
      throw new Error('No access token available');
    }
    return token;
  }, [tokenStorage]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    clearError,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Auth0 Auth Provider Component
 * Used when VITE_USE_MOCK_AUTH=false for Auth0 authentication
 *
 * **Token Storage (B.1.4.2):**
 * In Auth0 mode, tokens are managed via httpOnly cookies set by the backend.
 * The Auth0 SDK's getAccessTokenSilently() method handles token retrieval
 * and automatic refresh. The CookieTokenStorage is a placeholder that
 * acknowledges tokens are not directly accessible from JavaScript.
 */
function Auth0AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    user: auth0User,
    error: auth0Error,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [error, setError] = useState<string | null>(null);

  // Map Auth0 user to Finova User type
  const user = useMemo<User | null>(() => {
    if (!auth0User) return null;
    return mapAuth0User(auth0User as Auth0UserProfile);
  }, [auth0User]);

  // Sync Auth0 error to local error state
  useEffect(() => {
    if (auth0Error) {
      setError(auth0Error.message);
    }
  }, [auth0Error]);

  /**
   * Login - redirects to Auth0 Universal Login
   */
  const login = useCallback(
    async (
      _email?: string,
      _password?: string,
      options?: LoginOptions
    ): Promise<void> => {
      await loginWithRedirect({
        appState: { returnTo: options?.returnTo || '/dashboard' },
      });
    },
    [loginWithRedirect]
  );

  /**
   * Logout via Auth0
   */
  const logout = useCallback(async (): Promise<void> => {
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [auth0Logout]);

  /**
   * Clear authentication errors
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Get access token for API calls
   */
  const getAccessToken = useCallback(async (): Promise<string> => {
    return await getAccessTokenSilently();
  }, [getAccessTokenSilently]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading: auth0Loading,
    error,
    login,
    logout,
    clearError,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Auth Provider Component
 * Wraps the application to provide authentication context
 * Automatically selects between Auth0 and mock auth based on environment
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const isMockAuth = useMockAuth();

  if (isMockAuth) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }

  return <Auth0AuthProvider>{children}</Auth0AuthProvider>;
}

/**
 * Hook to access auth context
 * Must be used within an AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
